import { useQuery } from '@tanstack/react-query'
import { CircleCheck, CircleX, TriangleAlert } from 'lucide-react'
import { type ReactNode } from 'react'

import { Section, Spinner } from '@aeris/ui'

import {
  fetchChannelDiagnostics,
  type ChannelDiagnosticsType,
} from '../lib/api.ts'
import { gatewayErrorMessage } from '../lib/gateway-errors.ts'

/**
 * "Este canal está funcionando?" — respondido por EVIDÊNCIA (F063).
 *
 * **Não existe um botão "testar conexão" de propósito.** A API nunca fala com
 * o gateway (arquitetura registrada desde a F050, confirmada de novo na
 * F055b: tudo passa pelo worker, pela fila), e um ping ao PostgREST provaria
 * uma coisa só — que a chave estava válida naquele segundo. Não provaria que
 * o webhook chega, que o worker está de pé, nem que o índice externo está
 * preenchido.
 *
 * O banco já sabe mais do que esse ping saberia. Cada linha abaixo é uma
 * leitura do que o Aeris registrou, com o instante em que registrou.
 *
 * A ordem é a de DEPENDÊNCIA, não a de importância: sem credencial nada sai,
 * sem índice externo nada entra, sem worker nada é processado. Quem lê de
 * cima para baixo para no primeiro ✗ e sabe o que consertar.
 */

type Check = {
  readonly tone: 'ok' | 'warn' | 'bad'
  readonly label: string
  readonly detail: string
}

const ICON: Record<Check['tone'], ReactNode> = {
  ok: <CircleCheck aria-hidden="true" className="h-4 w-4 text-(--success)" />,
  warn: (
    <TriangleAlert aria-hidden="true" className="h-4 w-4 text-(--warning)" />
  ),
  bad: <CircleX aria-hidden="true" className="h-4 w-4 text-(--danger)" />,
}

/** "há 3 min", "há 2 h", "há 5 dias" — o instante importa menos que a distância dele. */
const since = (iso: string, now: Date): string => {
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - new Date(iso).getTime()) / 60_000)
  )
  if (minutes < 1) return 'agora há pouco'
  if (minutes < 60) return `há ${String(minutes)} min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `há ${String(hours)} h`
  return `há ${String(Math.round(hours / 24))} dias`
}

const checksFor = (
  diagnostics: ChannelDiagnosticsType,
  now: Date
): readonly Check[] => {
  const checks: Check[] = []

  checks.push(
    diagnostics.hasCredentials
      ? {
          tone: 'ok',
          label: 'Credenciais configuradas',
          detail: 'A chave do OpenBSP está guardada, cifrada.',
        }
      : {
          tone: 'bad',
          label: 'Sem credenciais',
          detail:
            'Nenhuma mensagem sai desta conta enquanto a chave do OpenBSP não for informada. Edite o canal e cole a chave.',
        }
  )

  checks.push(
    diagnostics.externalIndexPresent
      ? {
          tone: 'ok',
          label: 'Índice externo preenchido',
          detail:
            'O Aeris sabe a quem pertence um evento que chegar por este número.',
        }
      : {
          tone: 'bad',
          label: 'Índice externo VAZIO',
          detail:
            'Um evento que chegar por este número não vai ser atribuído a ninguém — ele é aceito, gravado, e some. Recadastre o canal para preencher o índice.',
        }
  )

  checks.push(
    diagnostics.lastEventAt === null
      ? {
          tone: 'warn',
          label: 'Nenhum evento recebido ainda',
          detail:
            'O webhook nunca chegou. No OpenBSP o webhook é POR ORGANIZAÇÃO: o cadastro precisa ser o desta organização, com a tabela "messages" e a operação "insert" marcadas — se faltar qualquer um dos três, o gatilho não dispara e nada é enviado. Confira também a URL desta tela e o segredo, que tem de ser o mesmo dos dois lados.',
        }
      : {
          tone: 'ok',
          label: `Último evento ${since(diagnostics.lastEventAt, now)}`,
          detail:
            diagnostics.lastEventKind === null
              ? 'O webhook está chegando.'
              : `Tipo: ${diagnostics.lastEventKind}. O webhook está chegando.`,
        }
  )

  if (diagnostics.unprocessedEventCount > 0) {
    checks.push({
      tone: 'warn',
      label: `${String(diagnostics.unprocessedEventCount)} eventos recebidos e não processados`,
      detail:
        'O webhook chega, mas nada os transforma em conversa. Quase sempre é o worker parado — confira se ele está no ar.',
    })
  }

  if (diagnostics.failedEventCount > 0) {
    checks.push({
      tone: 'bad',
      label: `${String(diagnostics.failedEventCount)} eventos falharam ao processar`,
      detail:
        'O worker tentou e não conseguiu. O motivo de cada um fica gravado no evento.',
    })
  }

  checks.push(
    diagnostics.lastInboundAt === null
      ? {
          tone: 'warn',
          label: 'Nenhuma mensagem recebida de cliente',
          detail:
            'Mande uma mensagem de um celular para este número — é o teste de ponta a ponta que nenhum diagnóstico substitui.',
        }
      : {
          tone: 'ok',
          label: `Última mensagem do cliente ${since(diagnostics.lastInboundAt, now)}`,
          detail: 'A entrada funciona de ponta a ponta.',
        }
  )

  checks.push(
    diagnostics.lastOutboundAt === null
      ? {
          tone: 'warn',
          label: 'Nenhuma mensagem enviada com sucesso',
          detail:
            'A saída nunca foi exercitada. Responda uma conversa para provar o caminho inteiro.',
        }
      : {
          tone: 'ok',
          label: `Último envio ${since(diagnostics.lastOutboundAt, now)}`,
          detail: 'A saída funciona de ponta a ponta.',
        }
  )

  if (diagnostics.recentFailedMessageCount > 0) {
    checks.push({
      tone: 'bad',
      label: `${String(diagnostics.recentFailedMessageCount)} envios recusados nas últimas 24 h`,
      detail:
        diagnostics.recentFailedErrorCode === null
          ? 'O gateway recusou sem devolver um código.'
          : gatewayErrorMessage(diagnostics.recentFailedErrorCode),
    })
  }

  if (diagnostics.lastError !== null) {
    checks.push({
      tone: 'bad',
      label: 'Último erro da conta',
      detail: diagnostics.lastError,
    })
  }

  if (diagnostics.status === 'disconnected') {
    checks.push({
      tone: 'bad',
      label: 'Canal marcado como desconectado',
      detail:
        'Alguém desligou este canal, ou o gateway avisou que o vínculo caiu. Nada entra nem sai enquanto estiver assim.',
    })
  }

  return checks
}

export const ChannelDiagnostics = ({
  accountId,
}: {
  accountId: string
}): ReactNode => {
  const diagnostics = useQuery({
    queryKey: ['communication-channel-diagnostics', accountId],
    queryFn: async ({ signal }) =>
      await fetchChannelDiagnostics(accountId, signal),
  })

  if (diagnostics.isPending) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }

  if (diagnostics.isError) {
    return (
      <p className="text-(length:--text-sm) text-(--text-muted)">
        Não foi possível carregar o diagnóstico deste canal.
      </p>
    )
  }

  const checks = checksFor(diagnostics.data, new Date())

  return (
    <Section
      title="Está funcionando?"
      description="Cada linha é o que o Aeris registrou de verdade — não um teste de conexão. Um teste verde não provaria que o webhook chega; um evento recebido prova."
    >
      <ul className="flex flex-col gap-2">
        {checks.map((check) => (
          <li key={check.label} className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0">{ICON[check.tone]}</span>
            <span className="flex flex-col">
              <span className="text-(length:--text-sm) text-(--text-primary)">
                {check.label}
              </span>
              <span className="text-(length:--text-xs) text-(--text-muted)">
                {check.detail}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Section>
  )
}
